import React, { useState, useEffect, useRef } from 'react';
import { getExerciseTemplateDetails, getExerciseTemplateByName } from '../lib/database';
import { useScrollLock } from '../hooks/useScrollLock';
import './ExerciseDetailModal.css';

// Badge per il tipo di dato muscolare (come da decisione UI)
const DATA_TYPE_BADGE = {
  EMG:          { label: 'EMG',               cls: 'bg-green-100 text-green-700' },
  biomeccanica: { label: 'Stima biomeccanica', cls: 'bg-blue-100 text-blue-700' },
  stima:        { label: 'Stima prudente',    cls: 'bg-gray-100 text-gray-600' },
};

function ExerciseDetailModal({ exercise, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSourceIdx, setOpenSourceIdx] = useState(null); // riga "Fonte" aperta

  useScrollLock(!!exercise);

  // Swipe to close state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const modalRef = useRef(null);
  const handleRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);

  useEffect(() => {
    const loadDetails = async () => {
      if (!exercise?.name) {
        setLoading(false);
        setDetails(null);
        return;
      }

      try {
        setLoading(true);
        setOpenSourceIdx(null);
        let data = null;

        // Prima prova con exercise_template_id se disponibile
        if (exercise.exercise_template_id) {
          try {
            data = await getExerciseTemplateDetails(exercise.exercise_template_id);
          } catch (err) {
            console.log('Template by ID not found, trying by name...');
          }
        }

        // Se non trovato, cerca per nome
        if (!data) {
          data = await getExerciseTemplateByName(exercise.name);
        }

        // Verifica che abbia almeno un dettaglio utile
        if (data && (data.description || data.execution_tips || data.common_mistakes ||
            data.muscles_involved || data.primary_muscle_group || data.anatomical_focus ||
            data.exercise_secondary_muscles?.length)) {
          setDetails(data);
        } else {
          setDetails(null);
        }
      } catch (err) {
        console.error('Error loading exercise details:', err);
        setDetails(null);
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [exercise]);

  // Close modal on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle touch/drag start - ONLY from handle area
  const handleDragStart = (e, fromHandle = false) => {
    // Only allow drag from the handle area
    if (!fromHandle) {
      return;
    }

    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    startY.current = clientY;
    currentY.current = clientY;
    setIsDragging(true);
  };

  // Handle touch/drag move
  const handleDragMove = (e) => {
    if (!isDragging) return;

    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    currentY.current = clientY;
    const delta = clientY - startY.current;

    // Only allow dragging down (positive delta)
    if (delta > 0) {
      setDragY(delta);
    }
  };

  // Handle touch/drag end
  const handleDragEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);
    const delta = currentY.current - startY.current;

    // If dragged more than 100px down, close the modal
    if (delta > 100) {
      onClose();
    } else {
      // Snap back
      setDragY(0);
    }
  };

  // Add mouse event listeners for desktop drag
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e);
      const handleMouseUp = () => handleDragEnd();

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  if (!exercise) return null;

  // Calculate opacity for backdrop based on drag
  const backdropOpacity = Math.max(0, 1 - dragY / 300);
  const modalTransform = `translateY(${dragY}px)`;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black z-40 animate-fade-in"
        style={{ opacity: backdropOpacity * 0.5 }}
        onClick={onClose}
      />

      {/* Modal/Drawer */}
      <div className="fixed inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center z-50">
        <div
          ref={modalRef}
          className={`exercise-modal-content bg-white rounded-t-3xl md:rounded-2xl max-w-2xl w-full max-h-[85vh] md:max-h-[90vh] overflow-y-auto shadow-2xl ${!isDragging ? 'animate-slide-up' : ''}`}
          style={{
            transform: modalTransform,
            transition: isDragging ? 'none' : 'transform 0.3s ease-out'
          }}
          onTouchMove={isDragging ? handleDragMove : undefined}
          onTouchEnd={isDragging ? handleDragEnd : undefined}
        >
          {/* Drag Handle - ONLY this area triggers drag */}
          <div
            ref={handleRef}
            className="drag-handle-container"
            onMouseDown={(e) => handleDragStart(e, true)}
            onTouchStart={(e) => handleDragStart(e, true)}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="drag-handle" />
            <p className="drag-hint">Scorri verso il basso per chiudere</p>
          </div>

          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-2xl font-extrabold text-gray-900">
              {exercise.name}
            </h2>
            <button
              onClick={onClose}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onClose();
              }}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              aria-label="Chiudi"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6">
            {loading && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                <p className="mt-4 text-gray-600">Caricamento dettagli...</p>
              </div>
            )}

            {!loading && !details && (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-3xl">📝</span>
                </div>
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Dettagli in arrivo
                </h3>
                <p className="text-gray-500 text-sm max-w-xs mx-auto">
                  Le istruzioni dettagliate, video dimostrativi e consigli per questo esercizio saranno presto disponibili.
                </p>
                {exercise.notes && (
                  <div className="mt-6 text-left bg-gray-50 rounded-xl p-4">
                    <h4 className="text-sm font-bold text-gray-600 uppercase mb-2">Note del Coach</h4>
                    <p className="text-gray-700">{exercise.notes}</p>
                  </div>
                )}
              </div>
            )}

            {!loading && details && (
              <>
                {/* Media */}
                {details.media_url && (
                  <div className="w-full flex justify-center bg-gray-50 rounded-xl p-4">
                    {details.media_type === 'video' ? (
                      <video
                        src={details.media_url}
                        controls
                        className="max-w-full max-h-64 md:max-h-80 rounded-lg"
                      />
                    ) : (
                      <img
                        src={details.media_url}
                        alt={details.name}
                        className="max-w-full max-h-64 md:max-h-80 object-contain rounded-lg"
                        onError={(e) => {
                          e.target.parentElement.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Coinvolgimento Muscolare (primario 100% + secondari con provenienza) */}
                {details.primary_muscle_group && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-500 uppercase mb-2">
                      Coinvolgimento Muscolare
                    </h3>
                    <div className="space-y-2">
                      {/* Primario */}
                      <div className="flex items-center gap-3 bg-primary-50 rounded-xl px-3 py-2">
                        <span className="text-sm font-bold text-primary-700 flex-1">{details.primary_muscle_group}</span>
                        <span className="text-[11px] font-semibold text-primary-600 uppercase">Principale</span>
                        <span className="text-sm font-extrabold text-primary-700 w-12 text-right">100%</span>
                      </div>
                      {/* Secondari */}
                      {[...(details.exercise_secondary_muscles || [])]
                        .sort((a, b) => b.involvement_percentage - a.involvement_percentage)
                        .map((sm, idx) => {
                          const badge = DATA_TYPE_BADGE[sm.source] || DATA_TYPE_BADGE.stima;
                          const isOpen = openSourceIdx === idx;
                          return (
                            <div key={idx} className="bg-gray-50 rounded-xl px-3 py-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-700 flex-1">{sm.muscle_group}</span>
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${badge.cls}`}>{badge.label}</span>
                                {sm.source_ref && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setOpenSourceIdx(isOpen ? null : idx); }}
                                    onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setOpenSourceIdx(isOpen ? null : idx); }}
                                    className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full border text-xs font-bold italic transition-colors ${isOpen ? 'bg-primary-100 text-primary-700 border-primary-200' : 'bg-white text-gray-500 border-gray-200 active:bg-gray-100'}`}
                                    aria-label="Mostra fonte"
                                    aria-expanded={isOpen}
                                    title="Fonte"
                                  >
                                    i
                                  </button>
                                )}
                                <span className="text-sm font-bold text-gray-600 w-12 text-right">{sm.involvement_percentage}%</span>
                              </div>
                              {isOpen && sm.source_ref && (
                                <p
                                  className="mt-2 text-xs text-gray-500 leading-snug border-t border-gray-200 pt-2"
                                  style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                >
                                  <span className="font-semibold text-gray-600">Fonte:</span> {sm.source_ref}
                                </p>
                              )}
                            </div>
                          );
                        })}
                    </div>
                    <p className="mt-2 text-[11px] text-gray-400">
                      Percentuali normalizzate al volume interno (non %MVIC assoluto).
                    </p>
                  </div>
                )}

                {/* Focus Muscolare */}
                {(details.anatomical_focus || details.muscles_involved) && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-500 uppercase mb-2">
                      Focus Muscolare
                    </h3>
                    <p className="text-base text-gray-700 leading-relaxed">
                      {details.anatomical_focus || details.muscles_involved}
                    </p>
                  </div>
                )}

                {/* Description */}
                {details.description && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-500 uppercase mb-2">
                      Descrizione
                    </h3>
                    <p className="text-base text-gray-700 leading-relaxed">
                      {details.description}
                    </p>
                  </div>
                )}

                {/* Execution Tips */}
                {details.execution_tips && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-500 uppercase mb-2">
                      Come Eseguire Correttamente
                    </h3>
                    <ul className="space-y-2">
                      {details.execution_tips.split('\n').filter(Boolean).map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-base text-gray-700">
                          <span className="text-success-500 font-bold flex-shrink-0">✓</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Common Mistakes */}
                {details.common_mistakes && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-500 uppercase mb-2">
                      Errori Comuni da Evitare
                    </h3>
                    <ul className="space-y-2">
                      {details.common_mistakes.split('\n').filter(Boolean).map((mistake, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-base text-gray-700">
                          <span className="text-red-500 font-bold flex-shrink-0">✕</span>
                          <span>{mistake}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ExerciseDetailModal;
